// frontend/src/components/admin/InvoicesManager.jsx
// ============================================================================
// GESTIONNAIRE DE FACTURES — HoraPilot Super-Admin
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, CheckCircle2, Clock, XCircle,
  ChevronDown, Search, Building2, Calendar,
  Download, MessageCircle, RefreshCw, AlertCircle,
  X, Save, Eye, Filter, TrendingUp, Wallet, Zap, CreditCard,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// HELPERS
// ============================================================================

const fmt = (n) => {
  if (!n && n !== 0) return '—';
  return Number(n).toLocaleString('fr-FR') + ' FCFA';
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

const calcLicenceFinal = (prixBase, discountType, discountValue) => {
  if (!prixBase) return 0;
  const base = parseFloat(prixBase) || 0;
  const val  = parseFloat(discountValue) || 0;
  if (discountType === 'percent') return Math.max(0, base * (1 - val / 100));
  return Math.max(0, base - val);
};

const STATUT_CONFIG = {
  en_attente: { label: 'En attente', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: Clock        },
  payee:      { label: 'Payée',      color: '#10b981', bg: 'rgba(16,185,129,0.08)', icon: CheckCircle2 },
  annulee:    { label: 'Annulée',    color: '#ef4444', bg: 'rgba(239,68,68,0.08)',  icon: XCircle      },
};

// ============================================================================
// BADGE STATUT
// ============================================================================
const StatutBadge = ({ statut }) => {
  const c = STATUT_CONFIG[statut] || STATUT_CONFIG.en_attente;
  const Icon = c.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99,
      background: c.bg, color: c.color,
      fontSize: 11, fontWeight: 700,
    }}>
      <Icon size={11} />
      {c.label}
    </span>
  );
};

// ============================================================================
// MODAL CRÉER FACTURE
// ============================================================================
const CreateInvoiceModal = ({ companies, onClose, onCreated, theme: t }) => {
  const [form, setForm] = useState({
    company_id:        '',
    periode_debut:     '',
    periode_fin:       '',
    echeance_date:     '',
    notes:             '',
    payment_method_id: '',   // ← nouveau
  });
  const [company,         setCompany]         = useState(null);
  const [prixBase,        setPrixBase]        = useState(null);
  const [extensions,      setExtensions]      = useState([]);
  const [addons,          setAddons]          = useState({ employes: 0, utilisateurs: 0, prixEmp: 2500, prixUsr: 3500 });
  const [paymentMethods,  setPaymentMethods]  = useState([]);   // ← nouveau
  const [loadingPricing,  setLoadingPricing]  = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false); // ← nouveau
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Charger les modes de paiement au montage ──
  useEffect(() => {
    setLoadingPayments(true);
    superAdminApi.danicedSettings.getPaymentMethods()
      .then(res => {
        if (res.success) {
          const actifs = (res.data || []).filter(m => m.actif == 1);
          setPaymentMethods(actifs);
          // Pré-sélectionner le premier mode actif
          if (actifs.length > 0) {
            setForm(f => ({ ...f, payment_method_id: String(actifs[0].id) }));
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingPayments(false));
  }, []);

  // ── Charger prix licence + extensions quand l'entreprise change ──
  useEffect(() => {
    if (!form.company_id) {
      setCompany(null); setPrixBase(null); setExtensions([]);
      return;
    }
    const c = companies.find(c => String(c.id) === String(form.company_id));
    setCompany(c || null);
    if (!c) return;

    setLoadingPricing(true);
    Promise.all([
      superAdminApi.billing.getPrixBase(c.id).catch(() => null),
      superAdminApi.extensions.getByCompany(c.id).catch(() => null),
    ]).then(([pRes, eRes]) => {
      if (pRes?.success) {
        setPrixBase(pRes.data);
        setAddons({
          employes:     parseInt(pRes.data?.addon_employes)             || 0,
          utilisateurs: parseInt(pRes.data?.addon_utilisateurs)         || 0,
          prixEmp:      parseFloat(pRes.data?.prix_par_employe_sup)     || 2500,
          prixUsr:      parseFloat(pRes.data?.prix_par_utilisateur_sup) || 3500,
        });
        // Calculer automatiquement la date d'échéance depuis billing_day
        if (pRes.data?.billing_day) {
          const today  = new Date();
          const day    = parseInt(pRes.data.billing_day);
          let target   = new Date(today.getFullYear(), today.getMonth(), day);
          if (target <= today) {
            target = new Date(today.getFullYear(), today.getMonth() + 1, day);
          }
          setForm(f => ({ ...f, echeance_date: target.toISOString().split('T')[0] }));
        }
      }
      if (eRes?.success) setExtensions((eRes.data || []).filter(e => e.is_enabled == 1));
    }).finally(() => setLoadingPricing(false));
  }, [form.company_id, companies]);

  // Calculs
  const licenceFinal = prixBase
    ? calcLicenceFinal(prixBase.prix, prixBase.discount_type, prixBase.discount_value)
    : null;
  const totalExtensions = extensions.reduce((sum, e) => sum + (parseFloat(e.prix_mensuel) || 0), 0);
  const addonUsr        = addons.utilisateurs * addons.prixUsr;
  const addonEmp        = Math.ceil(addons.employes / 10) * addons.prixEmp;
  const totalAddons     = addonUsr + addonEmp;
  const montantTotal    = prixBase ? (licenceFinal ?? 0) + totalExtensions + totalAddons : null;
  const hasDiscount     = prixBase && (parseFloat(prixBase.discount_value) || 0) > 0;

  // Mode de paiement sélectionné (pour aperçu)
  const selectedPaymentMethod = paymentMethods.find(
    m => String(m.id) === String(form.payment_method_id)
  ) || null;

  const handleSave = async () => {
    setError('');
    if (!form.company_id)    return setError('Sélectionnez une entreprise.');
    if (!form.periode_debut) return setError('Saisissez la date de début.');
    if (!form.periode_fin)   return setError('Saisissez la date de fin.');
    if (!form.echeance_date) return setError("Saisissez la date d'échéance.");

    setSaving(true);
    try {
      const res = await superAdminApi.invoices.create({
        ...form,
        company_id:        parseInt(form.company_id),
        montant_final:     montantTotal,
        payment_method_id: form.payment_method_id ? parseInt(form.payment_method_id) : null, // ← nouveau
      });
      if (res.success) { onCreated(); onClose(); }
      else setError(res.message || 'Erreur lors de la création.');
    } catch (e) {
      setError('Erreur réseau.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: '100%', maxWidth: 860, borderRadius: 20,
        background: t.bgCard, border: `1px solid ${t.border}`,
        boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: t.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={17} style={{ color: t.primary }} />
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>Nouvelle facture</p>
              <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>Remplissez les informations de facturation</p>
            </div>
          </div>
          <button onClick={onClose} style={{ padding: 6, borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '20px 22px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

          {/* ── COLONNE GAUCHE : Entreprise + Récapitulatif ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Entreprise */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 }}>
              Entreprise *
            </label>
            <div style={{ position: 'relative' }}>
              <select
                value={form.company_id}
                onChange={e => set('company_id', e.target.value)}
                style={{
                  width: '100%', padding: '9px 36px 9px 12px', borderRadius: 10,
                  background: t.bgInput, border: `1px solid ${t.border}`,
                  color: form.company_id ? t.text : t.textMuted,
                  fontSize: 13, fontWeight: 500, appearance: 'none', cursor: 'pointer',
                }}
              >
                <option value="">— Sélectionner une entreprise —</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
              <ChevronDown size={14} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none' }} />
            </div>
          </div>

          {/* Récapitulatif pricing si entreprise sélectionnée */}
          {company && (
            <div style={{ borderRadius: 12, border: `1px solid ${t.border}`, overflow: 'hidden' }}>
              <div style={{
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
                background: t.primarySofter, borderBottom: `1px solid ${t.primaryBorder}`,
              }}>
                <Zap size={13} style={{ color: t.primary }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: t.primary }}>Récapitulatif mensuel</span>
                {loadingPricing && (
                  <span style={{
                    marginLeft: 'auto', width: 12, height: 12, borderRadius: '50%',
                    border: `2px solid ${t.primary}`, borderTopColor: 'transparent',
                    animation: 'spin 0.8s linear infinite', display: 'inline-block',
                  }} />
                )}
              </div>

              <div style={{ padding: '12px 14px', background: t.bgCard, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {prixBase ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: t.textSecondary }}>Licence {prixBase.type_licence || ''}</span>
                      <span style={{ fontWeight: 600, color: t.text }}>{fmt(prixBase.prix)}</span>
                    </div>
                    {hasDiscount && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                          <span style={{ color: '#f59e0b' }}>
                            Réduction {prixBase.discount_type === 'percent' ? `(${prixBase.discount_value}%)` : '(montant fixe)'}
                          </span>
                          <span style={{ fontWeight: 600, color: '#f59e0b' }}>
                            − {fmt(prixBase.discount_type === 'percent' ? (prixBase.prix * prixBase.discount_value / 100) : prixBase.discount_value)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, paddingBottom: 8, borderBottom: `1px dashed ${t.border}` }}>
                          <span style={{ fontWeight: 600, color: t.textSecondary }}>Prix licence final</span>
                          <span style={{ fontWeight: 700, color: t.text }}>{fmt(licenceFinal)}</span>
                        </div>
                      </>
                    )}
                    {!loadingPricing && extensions.map(ext => (
                      <div key={ext.flag_key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: t.textSecondary }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f97316', flexShrink: 0 }} />
                          {ext.label || ext.flag_key}
                        </span>
                        <span style={{ fontWeight: 600, color: '#f97316' }}>+ {fmt(ext.prix_mensuel)}</span>
                      </div>
                    ))}
                    {!loadingPricing && extensions.length === 0 && (
                      <div style={{ fontSize: 11, fontStyle: 'italic', color: t.textMuted }}>Aucune extension active</div>
                    )}
                    {/* Add-ons utilisateurs */}
                    {!loadingPricing && addons.utilisateurs > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: t.textSecondary }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                          {`Add-on ${addons.utilisateurs} utilisateur${addons.utilisateurs > 1 ? 's' : ''} sup.`}
                        </span>
                        <span style={{ fontWeight: 600, color: '#f59e0b' }}>+ {fmt(addonUsr)}</span>
                      </div>
                    )}
                    {/* Add-ons employés */}
                    {!loadingPricing && addons.employes > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: t.textSecondary }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                          {`Add-on ${addons.employes} employé${addons.employes > 1 ? 's' : ''} sup.`}
                        </span>
                        <span style={{ fontWeight: 600, color: '#f59e0b' }}>+ {fmt(addonEmp)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: `2px solid ${t.border}` }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: t.textSecondary }}>Total à facturer</span>
                      <span style={{ fontSize: 20, fontWeight: 800, color: t.primary }}>{fmt(montantTotal)}</span>
                    </div>
                    {prixBase.interlocuteur && (
                      <p style={{ fontSize: 11, color: t.textMuted, margin: '4px 0 0' }}>
                        Interlocuteur : <strong style={{ color: t.textSecondary }}>{prixBase.interlocuteur}</strong>
                      </p>
                    )}
                  </>
                ) : !loadingPricing ? (
                  <p style={{ fontSize: 12, color: t.textMuted, fontStyle: 'italic' }}>
                    Aucune tarification configurée pour cette entreprise.
                  </p>
                ) : null}
              </div>
            </div>
          )}

          </div> {/* fin colonne gauche */}

          {/* ── COLONNE DROITE : Formulaire ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Période */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Début de période *', key: 'periode_debut' },
              { label: 'Fin de période *',   key: 'periode_fin'   },
            ].map(({ label, key }) => (
              <div key={key}>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 }}>{label}</label>
                <input type="date" value={form[key]} onChange={e => set(key, e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
            ))}
          </div>

          {/* Échéance — calculée automatiquement depuis billing_day */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 }}>
              Date d'échéance
            </label>
            {prixBase?.billing_day ? (
              <div style={{
                padding: '9px 12px', borderRadius: 10,
                background: t.primarySofter, border: `1px solid ${t.primaryBorder}`,
                fontSize: 13, fontWeight: 600, color: t.primary,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Calendar size={14} />
                {form.echeance_date
                  ? new Date(form.echeance_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                  : '—'}
                <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 'auto' }}>
                  Jour {prixBase.billing_day} du mois
                </span>
              </div>
            ) : (
              <input type="date" value={form.echeance_date} onChange={e => set('echeance_date', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13 }}
              />
            )}
          </div>

          {/* ══ MODE DE PAIEMENT ══ */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 }}>
              Mode de paiement
            </label>

            {loadingPayments ? (
              <div style={{ padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, fontSize: 13, color: t.textMuted, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 12, height: 12, borderRadius: '50%', border: `2px solid ${t.primary}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                Chargement…
              </div>
            ) : paymentMethods.length === 0 ? (
              <div style={{ padding: '9px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 12, color: '#b45309', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={13} style={{ flexShrink: 0 }} />
                Aucun mode de paiement configuré — ajoutez-en dans <strong style={{ marginLeft: 4 }}>Nos paramètres</strong>
              </div>
            ) : (
              <>
                {/* Sélecteur */}
                <div style={{ position: 'relative' }}>
                  <select
                    value={form.payment_method_id}
                    onChange={e => set('payment_method_id', e.target.value)}
                    style={{
                      width: '100%', padding: '9px 36px 9px 36px', borderRadius: 10,
                      background: t.bgInput, border: `1px solid ${t.border}`,
                      color: t.text, fontSize: 13, fontWeight: 500,
                      appearance: 'none', cursor: 'pointer',
                    }}
                  >
                    <option value="">— Aucun mode sélectionné —</option>
                    {paymentMethods.map(m => (
                      <option key={m.id} value={m.id}>{m.libelle}</option>
                    ))}
                  </select>
                  <CreditCard size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none' }} />
                  <ChevronDown size={14} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none' }} />
                </div>

                {/* Aperçu du mode sélectionné */}
                {selectedPaymentMethod && (
                  <div style={{ marginTop: 8, padding: '10px 14px', borderRadius: 10, background: t.primarySofter, border: `1px solid ${t.primaryBorder}`, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: t.primary }}>{selectedPaymentMethod.libelle}</span>
                    {selectedPaymentMethod.numero_rib && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: t.text }}>{selectedPaymentMethod.numero_rib}</span>
                    )}
                    {selectedPaymentMethod.instructions && (
                      <span style={{ fontSize: 11, color: t.textMuted, fontStyle: 'italic' }}>{selectedPaymentMethod.instructions}</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Notes */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 }}>Notes internes (optionnel)</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Ex: Paiement confirmé par Jean le 10/03..."
              style={{ width: '100%', padding: '9px 12px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, resize: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Erreur */}
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
            </div>
          )}
          </div> {/* fin colonne droite */}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: `1px solid ${t.border}`, display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: 10, background: t.primary, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            {saving ? <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> : <Save size={14} />}
            {saving ? 'Création...' : 'Créer la facture'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODAL CONFIRMER PAIEMENT
// ============================================================================
const ConfirmPayModal = ({ invoice, onClose, onConfirmed, theme: t }) => {
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const res = await superAdminApi.invoices.markPaid(invoice.id);
      if (res.success) { onConfirmed(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}>
      <div style={{ width: '100%', maxWidth: 420, borderRadius: 20, background: t.bgCard, border: `1px solid ${t.border}`, boxShadow: '0 24px 60px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
        <div style={{ padding: '22px 22px 0', textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <CheckCircle2 size={24} style={{ color: '#10b981' }} />
          </div>
          <p style={{ fontSize: 16, fontWeight: 700, color: t.text, margin: '0 0 6px' }}>Confirmer le paiement</p>
          <p style={{ fontSize: 13, color: t.textMuted, margin: '0 0 4px' }}>
            <strong style={{ color: t.text }}>{invoice.client_nom}</strong>
          </p>
          <p style={{ fontSize: 22, fontWeight: 800, color: '#10b981', margin: '10px 0 0' }}>
            {fmt(invoice.montant_final)}
          </p>
          <p style={{ fontSize: 11, color: t.textMuted, margin: '4px 0 20px' }}>
            Facture {invoice.numero} · Échéance {fmtDate(invoice.echeance_date)}
          </p>
        </div>
        <div style={{ padding: '0 22px 22px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={handleConfirm} disabled={saving} style={{ flex: 1, padding: 10, borderRadius: 10, background: '#10b981', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            {saving ? <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> : <CheckCircle2 size={14} />}
            {saving ? 'Confirmation...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const InvoicesManager = () => {
  const t = useTheme();
  const [invoices,   setInvoices]   = useState([]);
  const [companies,  setCompanies]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [filter,     setFilter]     = useState('all');
  const [modal,      setModal]      = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, compRes] = await Promise.all([
        superAdminApi.invoices.getAll(),
        superAdminApi.companies.getAll(),
      ]);
      if (invRes.success)  setInvoices(invRes.data || []);
      if (compRes.success) setCompanies((compRes.data || []).filter(c => c.status !== 'inactive'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = invoices.filter(inv => {
    const matchSearch = !search || inv.client_nom?.toLowerCase().includes(search.toLowerCase()) || inv.numero?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || inv.statut === filter;
    return matchSearch && matchFilter;
  });

  const kpis = {
    total:      invoices.length,
    en_attente: invoices.filter(i => i.statut === 'en_attente').length,
    payee:      invoices.filter(i => i.statut === 'payee').length,
    ca:         invoices.filter(i => i.statut === 'payee').reduce((s, i) => s + parseFloat(i.montant_final || 0), 0),
  };

  const handleWhatsApp = (inv) => {
    const pdfUrl = `${window.location.origin}/api/invoices/${inv.id}/pdf`;
    const msg = encodeURIComponent(`Bonjour ${inv.client_interlocuteur || inv.client_nom},\n\nVeuillez trouver ci-joint votre facture ${inv.numero} d'un montant de ${fmt(inv.montant_final)}.\n\n${pdfUrl}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
    superAdminApi.invoices.markWhatsappSent(inv.id).catch(() => {});
  };

  const handlePdf = (inv) => {
    superAdminApi.invoices.getPdf(inv.id);
  };

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: '0 0 4px' }}>Facturation</h1>
          <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>Générez et gérez les factures par entreprise</p>
        </div>
        <button
          onClick={() => setModal('create')}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 10, background: t.primary, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          <Plus size={15} /> Nouvelle facture
        </button>
      </div>

      {/* KPI CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        {[
          { label: 'Total factures',  value: kpis.total,      icon: FileText,    color: t.primary   },
          { label: 'En attente',      value: kpis.en_attente, icon: Clock,       color: '#f59e0b'   },
          { label: 'Payées',          value: kpis.payee,      icon: CheckCircle2,color: '#10b981'   },
          { label: 'CA encaissé',     value: fmt(kpis.ca),    icon: Wallet,      color: t.primary, large: true },
        ].map(({ label, value, icon: Icon, color, large }) => (
          <div key={label} style={{ padding: '16px 18px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={14} style={{ color }} />
              </div>
            </div>
            <p style={{ fontSize: large ? 16 : 26, fontWeight: 800, color: t.text, margin: 0, lineHeight: 1 }}>{value}</p>
          </div>
        ))}
      </div>

      {/* FILTRES */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par entreprise ou n° facture..."
            style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        {['all', 'en_attente', 'payee', 'annulee'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{
              padding: '7px 14px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${filter === s ? t.primary : t.border}`,
              background: filter === s ? t.primarySoft : 'transparent',
              color: filter === s ? t.primary : t.textMuted,
              transition: 'all 0.15s',
            }}>
            {s === 'all' ? 'Toutes' : STATUT_CONFIG[s]?.label}
            {s !== 'all' && (
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800 }}>
                {invoices.filter(i => i.statut === s).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* TABLEAU */}
      <div style={{ borderRadius: 14, border: `1px solid ${t.border}`, overflow: 'hidden', background: t.bgCard }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '130px 1fr 110px 120px 110px 110px 160px',
          gap: 8, padding: '10px 16px',
          background: t.bgTableHeader, borderBottom: `1px solid ${t.border}`,
        }}>
          {['N° Facture', 'Entreprise', 'Période', 'Échéance', 'Montant', 'Statut', 'Actions'].map((h, i) => (
            <span key={h} style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: i === 6 ? 'right' : 'left' }}>
              {h}
            </span>
          ))}
        </div>

        {loading && (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <RefreshCw size={22} style={{ color: t.primary, animation: 'spin 1s linear infinite', display: 'inline-block' }} />
            <p style={{ fontSize: 13, color: t.textMuted, marginTop: 10 }}>Chargement des factures…</p>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <FileText size={32} style={{ color: t.textMuted, marginBottom: 10, display: 'block', margin: '0 auto 10px' }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: t.textSecondary }}>Aucune facture</p>
            <p style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>
              {search || filter !== 'all' ? 'Modifiez vos filtres.' : 'Créez votre première facture.'}
            </p>
          </div>
        )}

        {!loading && filtered.map((inv, i) => {
          const isPaid    = inv.statut === 'payee';
          const isPending = inv.statut === 'en_attente';
          return (
            <div key={inv.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '130px 1fr 110px 120px 110px 110px 160px',
                gap: 8, padding: '12px 16px',
                borderBottom: i < filtered.length - 1 ? `1px solid ${t.borderLight}` : 'none',
                alignItems: 'center', transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = t.bgTableRowHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: t.primary }}>{inv.numero}</span>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: '0 0 1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {inv.client_nom}
                </p>
                {inv.client_interlocuteur && (
                  <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{inv.client_interlocuteur}</p>
                )}
              </div>
              <div>
                <p style={{ fontSize: 11, color: t.textSecondary, margin: 0 }}>{fmtDate(inv.periode_debut)}</p>
                <p style={{ fontSize: 11, color: t.textMuted, margin: '1px 0 0' }}>→ {fmtDate(inv.periode_fin)}</p>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: isPending && new Date(inv.echeance_date) < new Date() ? '#ef4444' : t.textSecondary }}>
                {fmtDate(inv.echeance_date)}
              </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: t.text }}>{fmt(inv.montant_final)}</span>
              <StatutBadge statut={inv.statut} />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                {isPending && (
                  <button onClick={() => setModal({ type: 'pay', invoice: inv })} title="Marquer comme payée"
                    style={{ padding: '5px 10px', borderRadius: 7, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10b981', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircle2 size={12} /> Payée
                  </button>
                )}
                {isPaid && (
                  <button onClick={() => handlePdf(inv)} title="Télécharger PDF"
                    style={{ padding: '5px 9px', borderRadius: 7, background: t.primarySoft, border: `1px solid ${t.primaryBorder}`, color: t.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Download size={12} />
                  </button>
                )}
                {isPaid && (
                  <button onClick={() => handleWhatsApp(inv)} title="Envoyer sur WhatsApp"
                    style={{ padding: '5px 9px', borderRadius: 7, background: inv.whatsapp_sent ? 'rgba(16,185,129,0.08)' : 'rgba(37,211,102,0.1)', border: `1px solid ${inv.whatsapp_sent ? 'rgba(16,185,129,0.2)' : 'rgba(37,211,102,0.25)'}`, color: inv.whatsapp_sent ? '#10b981' : '#25d366', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <MessageCircle size={12} />
                    {inv.whatsapp_sent && <span style={{ fontSize: 10, fontWeight: 700 }}>✓</span>}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* MODALS */}
      {modal === 'create' && (
        <CreateInvoiceModal
          companies={companies}
          onClose={() => setModal(null)}
          onCreated={() => { load(); }}
          theme={t}
        />
      )}
      {modal?.type === 'pay' && (
        <ConfirmPayModal
          invoice={modal.invoice}
          onClose={() => setModal(null)}
          onConfirmed={() => { load(); }}
          theme={t}
        />
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default InvoicesManager;